import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  #handleSearchNavigation = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target instanceof HTMLElement ? event.target : null);
    const link = target?.closest('a[href]');

    if (!(link instanceof HTMLAnchorElement)) return;

    // Never intercept clicks in the inline results area.
    if (link.closest('.header__search-results')) return;

    // Only intercept links that are part of the header navigation/search action.
    if (!link.closest('.header__row') && !link.closest('.search-action')) return;

    const url = new URL(link.href, window.location.origin);
    if (url.pathname !== Theme.routes.search_url) return;

    event.preventDefault();
    this.toggleInlineSearch();
  };

  #inlineSearchTimeout = null;
  #lastInlineSearchTerm = '';
  #inlineSearchEventsController = null;
  #issuesSearchIndex = null;

  /**
   * @type {AbortController | null}
   */
  #inlineSearchAbortController = null;

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const borderBoxSize = Array.isArray(entry.borderBoxSize) ? entry.borderBoxSize[0] : entry.borderBoxSize;
    const blockSize = borderBoxSize?.blockSize ?? entry.contentRect?.height ?? entry.target?.offsetHeight;

    if (!blockSize) return;

    // We round to the nearest integer to avoid unnecessary reflows.
    const roundedHeaderHeight = Math.round(blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  toggleInlineSearch = () => {
    if (this.dataset.inlineSearchOpen === 'true') {
      this.closeInlineSearch();
      return;
    }

    this.dataset.inlineSearchOpen = 'true';
    requestAnimationFrame(() => {
      this.refs.inlineSearchInput?.focus();
    });

    const input = this.refs.inlineSearchInput;
    if (input instanceof HTMLInputElement && input.value.trim().length > 0) {
      this.#loadInlineSearchResults(input.value.trim());
    }
  };

  closeInlineSearch = () => {
    this.dataset.inlineSearchOpen = 'false';
    if (this.refs.inlineSearchInput instanceof HTMLInputElement) {
      this.refs.inlineSearchInput.value = '';
    }

    this.dataset.inlineSearchHasValue = 'false';
    this.#lastInlineSearchTerm = '';

    if (this.#inlineSearchAbortController) {
      this.#inlineSearchAbortController.abort();
      this.#inlineSearchAbortController = null;
    }

    if (this.refs.inlineSearchResults instanceof HTMLElement) {
      this.refs.inlineSearchResults.hidden = true;
      this.refs.inlineSearchResults.replaceChildren();
    }
  };

  submitInlineSearch = (event) => {
    const input = this.refs.inlineSearchInput;
    if (!(input instanceof HTMLInputElement)) return;

    const value = input.value.trim();
    this.dataset.inlineSearchHasValue = value.length > 0 ? 'true' : 'false';

    if (this.#inlineSearchTimeout) {
      clearTimeout(this.#inlineSearchTimeout);
      this.#inlineSearchTimeout = null;
    }

    if (!value.length) {
      if (this.refs.inlineSearchResults instanceof HTMLElement) {
        this.refs.inlineSearchResults.hidden = true;
        this.refs.inlineSearchResults.replaceChildren();
      }
      return;
    }

    this.#inlineSearchTimeout = window.setTimeout(() => {
      this.#loadInlineSearchResults(value);
    }, 180);
  };

  handleInlineSearchKeyDown = (event) => {
    const input = this.refs.inlineSearchInput;
    if (!(input instanceof HTMLInputElement)) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeInlineSearch();
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  preventInlineSearchSubmit = (event) => {
    event.preventDefault();
  };

  handleInlineSearchResultsClick = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target instanceof HTMLElement ? event.target : null);
    if (!target?.closest('a')) return;

    this.closeInlineSearch();
  };

  #escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  #escapeRegExp(value) {
    return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  #stripHtml(value) {
    return String(value || '').replace(/<[^>]*>/g, ' ');
  }

  #normalizeWhitespace(value) {
    return String(value || '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  #stripHeading(text, heading) {
    const headingText = String(heading || '').trim();
    if (!headingText) return text;

    const pattern = new RegExp(`\\b${this.#escapeRegExp(headingText)}\\b`, 'i');
    return this.#normalizeWhitespace(String(text || '').replace(pattern, ' '));
  }

  #findTermIndex(text, term) {
    if (!text || !term) return -1;
    return String(text).toLowerCase().indexOf(String(term).toLowerCase());
  }

  #highlightTerm(text, term) {
    const safeText = this.#escapeHtml(text || '');
    const safeTerm = String(term || '').trim();
    if (safeTerm.length < 2) return safeText;

    const pattern = new RegExp(`(${this.#escapeRegExp(safeTerm)})`, 'ig');
    return safeText.replace(pattern, '<strong>$1</strong>');
  }

  #buildSnippet(text, term, maxLength = 120) {
    const cleanText = this.#normalizeWhitespace(this.#stripHtml(text));
    if (!cleanText) return '';

    const safeTerm = String(term || '').trim();
    if (!safeTerm) {
      return this.#escapeHtml(cleanText.slice(0, maxLength));
    }

    const matchIndex = this.#findTermIndex(cleanText, safeTerm);
    let start = 0;
    let end = cleanText.length;

    if (matchIndex >= 0) {
      const context = Math.max(20, Math.floor((maxLength - safeTerm.length) / 2));
      start = Math.max(0, matchIndex - context);
      end = Math.min(cleanText.length, matchIndex + safeTerm.length + context);
    } else {
      end = Math.min(cleanText.length, maxLength);
    }

    let snippet = cleanText.slice(start, end);
    if (start > 0) snippet = `...${snippet}`;
    if (end < cleanText.length) snippet = `${snippet}...`;

    return this.#highlightTerm(snippet, safeTerm);
  }

  #getInfoPageLabel(page, searchTerm) {
    const term = String(searchTerm || '').trim();
    if (term.length < 2) return '';

    const url = String(page?.url || '');
    if (!/\/pages\/info\b/i.test(url)) return '';

    const bodyText = this.#normalizeWhitespace(
      this.#stripHtml(page?.body || page?.body_html || page?.content || page?.excerpt || '')
    );
    if (!bodyText) return '';

    const matchIndex = this.#findTermIndex(bodyText, term);
    if (matchIndex < 0) return '';

    const lowerBody = bodyText.toLowerCase();
    const aboutIndex = lowerBody.indexOf('about');
    const stockistsIndex = lowerBody.indexOf('stockists');

    if (stockistsIndex !== -1 && matchIndex >= stockistsIndex) return 'Stockists';
    if (aboutIndex !== -1 && (stockistsIndex === -1 || matchIndex < stockistsIndex)) return 'About';

    return '';
  }

  #renderResourceLinks(title, items = [], options = {}) {
    if (!items.length) return '';

    const { searchTerm = '', getContext = null, getLabel = null } = options;

    const links = items
      .map((item) => {
        const itemTitle = item.title || item.text || 'Untitled';
        const itemUrl = this.#escapeHtml(item.url || '#');
        const labelText = typeof getLabel === 'function' ? getLabel(item, searchTerm) : '';
        const label = labelText ? String(labelText).toUpperCase() : '';
        const hasLabel = Boolean(label);
        const contextSource = typeof getContext === 'function' ? getContext(item, searchTerm) : '';
        const contextClean = hasLabel ? this.#stripHeading(contextSource, labelText) : contextSource;
        const baseTitle = hasLabel ? String(itemTitle).toUpperCase() : itemTitle;
        const combinedTitle = hasLabel ? `${baseTitle} - ${label}` : itemTitle;
        const titleClass = hasLabel
          ? 'header-search-link-title header-search-link-title--label'
          : 'header-search-link-title';
        const titleHtml = this.#highlightTerm(combinedTitle, searchTerm);
        const contextHtml = contextClean ? this.#buildSnippet(contextClean, searchTerm, 90) : '';
        const contextMarkup = contextHtml
          ? `<span class="header-search-link-context">${contextHtml}</span>`
          : '';

        return `
          <li class="header-search-link">
            <a href="${itemUrl}">
              <span class="${titleClass}">${titleHtml}</span>
              ${contextMarkup}
            </a>
          </li>
        `;
      })
      .join('');

    return `
      <section class="header-search-group">
        <h4>${this.#escapeHtml(title)}</h4>
        <ul class="header-search-links list-unstyled">${links}</ul>
      </section>
    `;
  }

  #getVariantResults(products = [], searchTerm = '') {
    const term = String(searchTerm || '').trim().toLowerCase();
    const matchesTerm = (value) => {
      if (!term) return true;
      return String(value || '').toLowerCase().includes(term);
    };

    const results = [];

    products.forEach((product) => {
      const variants = Array.isArray(product?.variants) ? product.variants : [];

      if (!variants.length) {
        results.push({ product, variant: null });
        return;
      }

      const matchingVariants = variants.filter((variant) => matchesTerm(variant?.title));
      const variantsToUse = matchingVariants.length ? matchingVariants : variants;

      variantsToUse.forEach((variant) => {
        results.push({ product, variant });
      });
    });

    return results.slice(0, 8);
  }

  #getVariantDisplayTitle(product, variant) {
    const variantTitle = String(variant?.title || '').trim();
    if (!variantTitle || variantTitle.toLowerCase() === 'default title') {
      return product?.title || 'Product';
    }

    return variantTitle;
  }

  #getVariantMetaTitle(product, variant) {
    const variantTitle = String(variant?.title || '').trim();
    if (!variantTitle || variantTitle.toLowerCase() === 'default title') return '';
    return product?.title || '';
  }

  #getVariantImage(product, variant) {
    return (
      variant?.image?.url ||
      variant?.featured_image?.url ||
      product?.featured_image?.url ||
      product?.image ||
      ''
    );
  }

  #getVariantUrl(product, variant) {
    const baseUrl = String(product?.url || '#');
    if (!variant?.id || baseUrl === '#') return baseUrl;

    if (baseUrl.includes('?')) return `${baseUrl}&variant=${variant.id}`;
    return `${baseUrl}?variant=${variant.id}`;
  }

  #renderProductCards(products = [], searchTerm = '') {
    if (!products.length) return '';

    const variantResults = this.#getVariantResults(products, searchTerm);
    if (!variantResults.length) return '';

    const cards = variantResults
      .map(({ product, variant }) => {
        const displayTitle = this.#getVariantDisplayTitle(product, variant);
        const metaTitle = this.#getVariantMetaTitle(product, variant);
        const url = this.#escapeHtml(this.#getVariantUrl(product, variant));
        const image = this.#getVariantImage(product, variant);
        const imageHtml = image
          ? `<img src="${this.#escapeHtml(image)}" alt="${this.#escapeHtml(displayTitle)}" loading="lazy">`
          : '';
        const priceValue = variant?.price || product?.price;
        const price = priceValue
          ? `<p class="header-search-card-price">${this.#escapeHtml(priceValue)}</p>`
          : '';
        const titleHtml = this.#highlightTerm(displayTitle, searchTerm);
        const metaHtml = metaTitle
          ? `<p class="header-search-card-meta">${this.#highlightTerm(metaTitle, searchTerm)}</p>`
          : '';

        return `
          <li class="header-search-card">
            <a href="${url}">
              <div class="header-search-card-media">${imageHtml}</div>
              <div class="header-search-card-content">
                <p class="header-search-card-title">${titleHtml}</p>
                ${metaHtml}
                ${price}
              </div>
            </a>
          </li>
        `;
      })
      .join('');

    return `
      <section class="header-search-group">
        <h4>Products</h4>
        <ul class="header-search-products list-unstyled">${cards}</ul>
      </section>
    `;
  }

  #normalizeSearchText(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  #toUrlKey(url) {
    try {
      const parsed = new URL(url || '', location.origin);
      return `${parsed.pathname}${parsed.search}`;
    } catch {
      return String(url || '');
    }
  }

  #getIssuesSearchIndex() {
    if (Array.isArray(this.#issuesSearchIndex)) return this.#issuesSearchIndex;

    const node = document.getElementById('issues-search-index');
    if (!(node instanceof HTMLScriptElement) || !node.textContent) {
      this.#issuesSearchIndex = [];
      return this.#issuesSearchIndex;
    }

    try {
      const parsed = JSON.parse(node.textContent);
      this.#issuesSearchIndex = Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('Failed to parse Issues search index', error);
      this.#issuesSearchIndex = [];
    }

    return this.#issuesSearchIndex;
  }

  #getIssueFallbackResults(searchTerm, products = []) {
    const normalizedTerm = this.#normalizeSearchText(searchTerm);
    if (normalizedTerm.length < 2) return [];

    const issuesIndex = this.#getIssuesSearchIndex();
    if (!issuesIndex.length) return [];

    const existingProductUrls = new Set(
      products.map((product) => this.#toUrlKey(product?.url || '')).filter(Boolean)
    );

    return issuesIndex
      .filter((issue) => {
        const haystack = this.#normalizeSearchText(`${issue?.title || ''} ${issue?.searchText || ''}`);
        if (!haystack.includes(normalizedTerm)) return false;

        const issueUrlKey = this.#toUrlKey(issue?.url || '');
        return issueUrlKey && !existingProductUrls.has(issueUrlKey);
      })
      .slice(0, 6)
      .map((issue) => ({
        title: issue.title || 'Issue',
        url: issue.url || '#',
        searchText: issue.searchText || '',
      }));
  }

  async #loadInlineSearchResults(searchTerm) {
    const resultsContainer = this.refs.inlineSearchResults;
    if (!(resultsContainer instanceof HTMLElement)) return;

    if (!this.dataset.inlineSearchOpen || this.dataset.inlineSearchOpen !== 'true') return;

    if (this.#lastInlineSearchTerm === searchTerm) return;
    this.#lastInlineSearchTerm = searchTerm;

    if (this.#inlineSearchAbortController) {
      this.#inlineSearchAbortController.abort();
    }

    const abortController = new AbortController();
    this.#inlineSearchAbortController = abortController;

    resultsContainer.hidden = false;
    resultsContainer.innerHTML = '<div class="header-search-error">Loading results...</div>';

    try {
      const predictiveSearchRoute = Theme?.routes?.predictive_search_url || '/search/suggest';
      const jsonRoute = predictiveSearchRoute.endsWith('.json') ? predictiveSearchRoute : `${predictiveSearchRoute}.json`;
      const url = new URL(jsonRoute, location.origin);
      url.searchParams.set('q', searchTerm);
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[type]', 'product,page,article');
      url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,tag,vendor,body');

      const response = await fetch(url.toString(), { signal: abortController.signal });
      if (!response.ok || abortController.signal.aborted) return;

      const json = await response.json();
      const results = json?.resources?.results;

      if (!results) {
        resultsContainer.innerHTML = '<div class="header-search-error">No search results returned.</div>';
        return;
      }

      const issueFallbackResults = this.#getIssueFallbackResults(searchTerm, results.products || []);

      const markup = [
        this.#renderProductCards(results.products || [], searchTerm),
        this.#renderResourceLinks('Issues', issueFallbackResults, {
          searchTerm,
          getContext: (item) => item.searchText || '',
        }),
        this.#renderResourceLinks('Pages', results.pages || [], {
          searchTerm,
          getContext: (item) => item.body || item.body_html || item.content || item.excerpt || '',
          getLabel: (item) => this.#getInfoPageLabel(item, searchTerm),
        }),
        this.#renderResourceLinks('Articles', results.articles || [], {
          searchTerm,
          getContext: (item) => item.body || item.body_html || item.content || item.excerpt || '',
        }),
      ]
        .filter(Boolean)
        .join('');

      resultsContainer.innerHTML =
        markup || '<div class="header-search-error">No results found. Try another search.</div>';
      resultsContainer.hidden = false;
    } catch (error) {
      if (abortController.signal.aborted) return;
      console.error('Inline search failed to load results', error);
      resultsContainer.innerHTML = '<div class="header-search-error">Search is temporarily unavailable.</div>';
      resultsContainer.hidden = false;
    }
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.addEventListener('click', this.#handleSearchNavigation);

    this.#inlineSearchEventsController = new AbortController();
    const { signal } = this.#inlineSearchEventsController;
    this.refs.inlineSearchInput?.addEventListener('input', this.submitInlineSearch, { signal });
    this.refs.inlineSearchInput?.addEventListener('keydown', this.handleInlineSearchKeyDown, { signal });
    this.querySelector('.header-search-form')?.addEventListener('submit', this.preventInlineSearchSubmit, { signal });
    this.refs.inlineSearchResults?.addEventListener('click', this.handleInlineSearchResultsClick, { signal });

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.removeEventListener('click', this.#handleSearchNavigation);
    if (this.#inlineSearchEventsController) {
      this.#inlineSearchEventsController.abort();
      this.#inlineSearchEventsController = null;
    }
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#inlineSearchTimeout) {
      clearTimeout(this.#inlineSearchTimeout);
      this.#inlineSearchTimeout = null;
    }
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  // Block direct navigation to the search route; search should stay in the header UI.
  const normalizePath = (path) => {
    const normalized = String(path || '').trim();
    if (!normalized) return '/';
    return normalized.replace(/\/+$/, '') || '/';
  };

  const currentPath = normalizePath(window.location.pathname);
  const searchPath = normalizePath(Theme?.routes?.search_url || '/search');

  if (currentPath === searchPath) {
    const redirectTarget = normalizePath(Theme?.routes?.root_url || '/');
    if (redirectTarget !== searchPath) {
      window.location.replace(redirectTarget);
      return;
    }
  }

  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');
  /** @param {HTMLElement} element */
  const isIssueMarqueeSection = (element) =>
    element.classList.contains('header-issue-marquee') || element.querySelector('.header-issue-marquee') !== null;
  const calculateIssueMarqueeHeight = () => {
    if (!(headerGroup instanceof HTMLElement)) return 0;

    let totalHeight = 0;
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (!(element instanceof HTMLElement) || !isIssueMarqueeSection(element)) continue;
      totalHeight += element.offsetHeight;
    }

    return totalHeight;
  };

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (!(entry.target instanceof HTMLElement) || isIssueMarqueeSection(entry.target)) {
          return totalHeight;
        }

        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
      document.body.style.setProperty('--header-issue-marquee-height', `${calculateIssueMarqueeHeight()}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
